import { useEffect, useRef } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { differenceInMilliseconds } from 'date-fns';
import { useIsAuthenticated } from '@azure/msal-react';

const maxSessionHours = 8;
const maxSessionMs = maxSessionHours * 60 * 60 * 1000;

const BASE_STORAGE_KEY = 'app_maxalive_activity';

const initiateSession = (accountId: string): void => {
  const sessionDate = localStorage.getItem(`${BASE_STORAGE_KEY}_${accountId}`);
  if (sessionDate) return;
  localStorage.setItem(`${BASE_STORAGE_KEY}_${accountId}`, new Date().toISOString());
};

const clearSession = (accountId: string): void => { 
  localStorage.removeItem(`${BASE_STORAGE_KEY}_${accountId}`);
};

const hasExpiredSession = (accountId: string): boolean => {
  const sessionDate = localStorage.getItem(`${BASE_STORAGE_KEY}_${accountId}`);
  if (sessionDate) return differenceInMilliseconds(new Date(), new Date(sessionDate)) >= maxSessionMs;
  return false;
};

export const useMsalSessionManagement = (
  instance: IPublicClientApplication,
  onExpired: () => void
) => {
  const activeAccount = instance.getActiveAccount();
  const isAuthenticated = useIsAuthenticated();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!activeAccount || !isAuthenticated) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      return;
    }

    const accountId = activeAccount.localAccountId;

    if (hasExpiredSession(accountId)) {
      clearSession(accountId);
      initiateSession(accountId);
      onExpired();
      return;
    }

    initiateSession(accountId);
  }, [isAuthenticated]);
};
